import React, { useState, useEffect } from 'react';
import { X, Car, Bike, Plus } from 'lucide-react';
import { TrackImage, GlobalCategory, Event } from '../../hooks/useEventManagement';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/currencyUtils';

interface EventModalProps {
  selectedTrackImage: TrackImage | null;
  setSelectedTrackImage: (image: TrackImage | null) => void;
  showEventModal: boolean;
  setShowEventModal: (show: boolean) => void;
  selectedEventDate: string | null;
  setSelectedEventDate: (date: string) => void;
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
  selectedVehicleFilter: 'car' | 'bike';
  setSelectedVehicleFilter: (filter: 'car' | 'bike') => void;
  applyToAllDates: boolean;
  setApplyToAllDates: (apply: boolean) => void;
  driftEventSettings: {
    registration_fee: number;
  };
  setDriftEventSettings: (settings: {
    registration_fee: number;
  }) => void;
  globalCategories: GlobalCategory[];
  events: Event[];
  fetchEvents: () => Promise<void>;
  fetchTrackImages: () => Promise<void>;
  formatDateForDisplay: (date: string) => string;
  fetchExistingEventCategories: (trackImageId: string) => Promise<string[]>;
}

const EventModal: React.FC<EventModalProps> = ({
  selectedTrackImage,
  setSelectedTrackImage,
  showEventModal,
  setShowEventModal,
  selectedEventDate,
  setSelectedEventDate,
  selectedCategories,
  setSelectedCategories,
  selectedVehicleFilter,
  setSelectedVehicleFilter,
  applyToAllDates,
  setApplyToAllDates,
  driftEventSettings,
  setDriftEventSettings,
  globalCategories,
  events,
  fetchEvents,
  fetchTrackImages,
  formatDateForDisplay,
  fetchExistingEventCategories
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [driftCategories, setDriftCategories] = useState<GlobalCategory[]>([]);
  const [selectedDriftCategories, setSelectedDriftCategories] = useState<string[]>([]);
  const [showDriftCategoryFilter, setShowDriftCategoryFilter] = useState(false);
  const [usePercentagePrizes, setUsePercentagePrizes] = useState(false);

  // Fetch drift categories when modal opens
  useEffect(() => {
    if (showEventModal && selectedTrackImage?.track_type === 'drift') {
      fetchDriftCategories();
    }
  }, [showEventModal, selectedTrackImage]);

  // Filter categories based on selected vehicle type
  const getFilteredCategories = () => {
    return globalCategories.filter(category => 
      category.vehicle_type === selectedVehicleFilter && 
      category.category_type === 'drag'
    );
  };

  // Fetch drift categories
  const fetchDriftCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('global_categories')
        .select('*')
        .eq('category_type', 'drift')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      setDriftCategories(data || []);
      
      // If we have a selected track image and it's a drift track, check for existing categories
      if (selectedTrackImage?.track_type === 'drift' && selectedTrackImage?.id) {
        const existingCategoryIds = await fetchExistingEventCategories(selectedTrackImage.id);
        setSelectedDriftCategories(existingCategoryIds);
      }
      
      setShowDriftCategoryFilter(data && data.length > 0);
    } catch (error) {
      console.error('Error fetching drift categories:', error);
      toast.error('Failed to load drift categories');
    }
  };

  const handleCreateEvent = async () => {
    if (!selectedTrackImage || !selectedEventDate) {
      toast.error('Please select an event date');
      return;
    }

    // For drift events, we don't require categories if using default settings
    const isDriftEvent = selectedTrackImage.track_type === 'drift';
    const hasDriftCategories = selectedDriftCategories.length > 0;
    
    if (!isDriftEvent && selectedCategories.length === 0) {
      toast.error('Please select at least one racing category');
      return;
    }

    setIsSubmitting(true);
    try {
      // Format the selected event date consistently
      const formattedEventDate = selectedEventDate;
      
      // Check if an event already exists for this track image and date
      let existingEvent = events.find(e => 
        e.track_image_id === selectedTrackImage.id && 
        e.event_date === formattedEventDate
      );

      let eventData;

      if (!existingEvent) {
        // Create new event
        const eventDate = new Date(formattedEventDate + 'T00:00:00.000Z');
        const registrationDeadline = new Date(eventDate);
        registrationDeadline.setDate(eventDate.getDate() - 2);

        const eventPayload: any = {
          track_image_id: selectedTrackImage.id,
          name: selectedTrackImage.title,
          description: selectedTrackImage.description,
          event_date: formattedEventDate,
          registration_deadline: registrationDeadline.toISOString().split('T')[0],
          is_active: true
        };

        const { data: newEventData, error: eventError } = await supabase
          .from('events')
          .insert([eventPayload])
          .select()
          .single();

        if (eventError) throw eventError;
        eventData = newEventData;
      } else {
        eventData = existingEvent;
      }

      // For drag events, handle categories
      if (!isDriftEvent && selectedCategories.length > 0) {
        // Get existing categories for this event
        const { data: existingCategories, error: existingCatError } = await supabase
          .from('event_categories')
          .select('global_category_id')
          .eq('event_id', eventData.id);

        if (existingCatError) throw existingCatError;

        const existingCategoryIds = existingCategories?.map(cat => cat.global_category_id) || [];
        
        // Only add categories that don't already exist
        const newCategoryIds = selectedCategories.filter(catId => !existingCategoryIds.includes(catId));

        if (newCategoryIds.length > 0) {
          // Add new categories to the event one by one to handle duplicates gracefully
          for (const categoryId of newCategoryIds) {
            const category = globalCategories.find(c => c.id === categoryId);
            const categoryRecord = {
              event_id: eventData.id,
              global_category_id: categoryId,
              name: category?.name || '',
              vehicle_type: category?.vehicle_type || 'car',
              registration_fee: category?.default_registration_fee || 0,
              first_place_prize: category?.default_first_place_prize || 0,
              second_place_prize: category?.default_second_place_prize || 0,
              first_place_percentage: category?.first_place_percentage || null,
              second_place_percentage: category?.second_place_percentage || null,
              use_percentage_prizes: category?.use_percentage_prizes || false
            };

            // Use insert with error handling for duplicates
            const { error: categoryError } = await supabase
              .from('event_categories')
              .insert([categoryRecord]);

            // Ignore duplicate key errors (23505), but throw other errors
            if (categoryError && categoryError.code !== '23505') {
              throw categoryError;
            }
          }
        }

        // Handle "Apply to all dates" logic - only add categories to existing events
        if (applyToAllDates && selectedTrackImage.event_dates && selectedTrackImage.event_dates.length > 1) {
          const otherDates = selectedTrackImage.event_dates
            .filter(ed => ed.event_date !== formattedEventDate)
            .map(ed => ed.event_date);

          for (const date of otherDates) {
            // Find existing event for this date (should already exist from EventDatesModal)
            const { data: existingEvent, error: checkError } = await supabase
              .from('events')
              .select('id')
              .eq('track_image_id', selectedTrackImage.id)
              .eq('event_date', date)
              .maybeSingle();

            if (checkError) throw checkError;

            // Only proceed if event exists (don't create new events here)
            if (existingEvent) {
              // Get existing categories for this event
              const { data: otherExistingCategories } = await supabase
                .from('event_categories')
                .select('global_category_id')
                .eq('event_id', existingEvent.id);

              const otherExistingCategoryIds = otherExistingCategories?.map(cat => cat.global_category_id) || [];
              
              // Only add selected categories that don't exist in this event
              const categoriesToAdd = selectedCategories.filter(catId => !otherExistingCategoryIds.includes(catId));

              if (categoriesToAdd.length > 0) {
                // Insert categories one by one to handle potential duplicates gracefully
                for (const categoryId of categoriesToAdd) {
                  const category = globalCategories.find(c => c.id === categoryId);
                  const categoryRecord = {
                    event_id: existingEvent.id,
                    global_category_id: categoryId,
                    name: category?.name || '',
                    vehicle_type: category?.vehicle_type || 'car',
                    registration_fee: category?.default_registration_fee || 0,
                    first_place_prize: category?.default_first_place_prize || 0,
                    second_place_prize: category?.default_second_place_prize || 0,
                    first_place_percentage: category?.first_place_percentage || null,
                    second_place_percentage: category?.second_place_percentage || null,
                    use_percentage_prizes: category?.use_percentage_prizes || false
                  };

                  // Use insert with error handling for duplicates
                  const { error: categoryError } = await supabase
                    .from('event_categories')
                    .insert([categoryRecord]);

                  // Ignore duplicate key errors (23505), but throw other errors
                  if (categoryError && categoryError.code !== '23505') {
                    throw categoryError;
                  }
                }
              }
            }
          }
        }

        if (newCategoryIds.length > 0 || applyToAllDates) {
          const totalDates = applyToAllDates && selectedTrackImage.event_dates ? selectedTrackImage.event_dates.length : 1;
          toast.success(
            `Categories updated successfully${applyToAllDates ? ` across ${totalDates} event${totalDates === 1 ? '' : 's'}` : ''}`
          );
        } else {
          toast.info('All selected categories were already added to this event');
        }
      } else if (isDriftEvent) {
        // For drift events, handle drift categories if selected
        if (hasDriftCategories) {
          // Get existing categories for this event
          const { data: existingCategories, error: existingCatError } = await supabase
            .from('event_categories')
            .select('global_category_id')
            .eq('event_id', eventData.id);

          if (existingCatError) throw existingCatError;

          const existingCategoryIds = existingCategories?.map(cat => cat.global_category_id) || [];
          
          // Only add categories that don't already exist
          const newCategoryIds = selectedDriftCategories.filter(catId => !existingCategoryIds.includes(catId));

          if (newCategoryIds.length > 0) {
            // Add new categories to the event one by one to handle duplicates gracefully
            for (const categoryId of newCategoryIds) {
              const category = driftCategories.find(c => c.id === categoryId);
              const categoryRecord = {
                event_id: eventData.id,
                global_category_id: categoryId,
                name: category?.name || '',
                vehicle_type: category?.vehicle_type || 'car',
                registration_fee: category?.default_registration_fee || 0,
                first_place_prize: category?.default_first_place_prize || 0,
                second_place_prize: category?.default_second_place_prize || 0,
                first_place_percentage: category?.first_place_percentage || null,
                second_place_percentage: category?.second_place_percentage || null,
                use_percentage_prizes: category?.use_percentage_prizes || false
              };

              // Use insert with error handling for duplicates
              const { error: categoryError } = await supabase
                .from('event_categories')
                .insert([categoryRecord]);

              // Ignore duplicate key errors (23505), but throw other errors
              if (categoryError && categoryError.code !== '23505') {
                throw categoryError;
              }
            }
          }
        }
        
        // Handle "Apply to all dates" for drift events - only add categories to existing events
        if (applyToAllDates && selectedTrackImage.event_dates && selectedTrackImage.event_dates.length > 1) {
          const otherDates = selectedTrackImage.event_dates
            .filter(ed => ed.event_date !== formattedEventDate)
            .map(ed => ed.event_date);

          for (const date of otherDates) {
            // Find existing event for this date (should already exist from EventDatesModal)
            const { data: existingEvent, error: checkError } = await supabase
              .from('events')
              .select('id')
              .eq('track_image_id', selectedTrackImage.id)
              .eq('event_date', date)
              .maybeSingle();

            if (checkError) throw checkError;

            // Only proceed if event exists and we have drift categories (don't create new events here)
            if (existingEvent && hasDriftCategories) {
              // Get existing categories for this event
              const { data: otherExistingCategories } = await supabase
                .from('event_categories')
                .select('global_category_id')
                .eq('event_id', existingEvent.id);

              const otherExistingCategoryIds = otherExistingCategories?.map(cat => cat.global_category_id) || [];
              
              // Only add selected drift categories that don't exist in this event
              const categoriesToAdd = selectedDriftCategories.filter(catId => !otherExistingCategoryIds.includes(catId));

              if (categoriesToAdd.length > 0) {
                // Insert categories one by one to handle potential duplicates gracefully
                for (const categoryId of categoriesToAdd) {
                  const category = driftCategories.find(c => c.id === categoryId);
                  const categoryRecord = {
                    event_id: existingEvent.id,
                    global_category_id: categoryId,
                    name: category?.name || '',
                    vehicle_type: category?.vehicle_type || 'car',
                    registration_fee: category?.default_registration_fee || 0,
                    first_place_prize: category?.default_first_place_prize || 0,
                    second_place_prize: category?.default_second_place_prize || 0,
                    first_place_percentage: category?.first_place_percentage || null,
                    second_place_percentage: category?.second_place_percentage || null,
                    use_percentage_prizes: category?.use_percentage_prizes || false
                  };

                  // Use insert with error handling for duplicates
                  const { error: categoryError } = await supabase
                    .from('event_categories')
                    .insert([categoryRecord]);

                  // Ignore duplicate key errors (23505), but throw other errors
                  if (categoryError && categoryError.code !== '23505') {
                    throw categoryError;
                  }
                }
              }
            }
          }
        }

        const totalDates = applyToAllDates && selectedTrackImage.event_dates ? selectedTrackImage.event_dates.length : 1;
        toast.success(`Created drift event${totalDates === 1 ? '' : 's'} for ${totalDates} date${totalDates === 1 ? '' : 's'}`);
      }
      
      setShowEventModal(false);
      setSelectedTrackImage(null);
      setSelectedCategories([]);
      setSelectedDriftCategories([]);
      setSelectedEventDate('');
      setApplyToAllDates(true);
      setSelectedVehicleFilter('car'); // Reset filter
      fetchTrackImages();
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter drift categories based on selected vehicle type
  const getFilteredDriftCategories = () => {
    return driftCategories.filter(category => category.vehicle_type === selectedVehicleFilter);
  };

  if (!showEventModal || !selectedTrackImage) return null;

  const isDriftEvent = selectedTrackImage.track_type === 'drift';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-zinc-900 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-white">
            {selectedTrackImage.track_type === 'drift' ? 'Create Drift Event' : 'Manage Racing Categories'}
          </h2>
          <button onClick={() => setShowEventModal(false)}>
            <X className="h-6 w-6 text-gray-400 hover:text-white" />
          </button>
        </div>
        
        <div className="mb-6 p-4 bg-zinc-800 rounded-lg">
          <p className="text-gray-300 text-sm">
            <strong>Flyer:</strong> {selectedTrackImage.title}
          </p>
          <p className="text-gray-400 text-xs mt-1">
            {selectedTrackImage.track_type === 'drift' 
              ? 'Create drift event with categories. You can add specific drift categories.'
              : 'Add or update racing categories for this drag event. Categories will be applied to the selected date.'
            }
          </p>
          {selectedTrackImage.track_type === 'drag' && selectedCategories.length > 0 && (
            <p className="text-green-400 text-xs mt-2">
              ✓ {selectedCategories.length} categories already selected for this event
            </p>
          )}
          {selectedTrackImage.track_type === 'drift' && selectedDriftCategories.length > 0 && (
            <p className="text-green-400 text-xs mt-2">
              ✓ {selectedDriftCategories.length} drift categories selected for this event
            </p>
          )}
        </div>

        <div className="space-y-6">
          {/* Event Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Event Date *
            </label>
            <select
              value={selectedEventDate || ''}
              onChange={(e) => setSelectedEventDate(e.target.value)}
              required
              className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white"
            >
              <option value="">Select event date</option>
              {selectedTrackImage.event_dates?.map((eventDate) => (
                <option key={eventDate.id} value={eventDate.event_date}>
                  {formatDateForDisplay(eventDate.event_date)}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Registration deadline will be automatically set to 2 days before this date.
            </p>
          </div>

          {/* Multi-date option */}
          {selectedTrackImage.event_dates && selectedTrackImage.event_dates.length > 1 && (
            <div className="bg-zinc-800 p-4 rounded-lg">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={applyToAllDates}
                  onChange={(e) => setApplyToAllDates(e.target.checked)}
                  className="mr-3 mt-1"
                />
                <div>
                  <span className="text-white font-medium">
                    {selectedTrackImage.track_type === 'drift' 
                      ? 'Apply drift event settings to all event dates'
                      : 'Apply new categories to all event dates'
                    }
                  </span>
                  <p className="text-gray-400 text-sm mt-1">
                    This event has {selectedTrackImage.event_dates.length} dates. Check this to apply 
                    {selectedTrackImage.track_type === 'drift' ? ' event settings' : ' new categories'} to all dates.
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Drift Categories Section */}
          {isDriftEvent && driftCategories.length > 0 && (
            <div className="bg-zinc-800 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white">Drift Categories</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedVehicleFilter('car')}
                    className={`px-3 py-1 rounded-lg transition-colors flex items-center gap-1 text-sm ${
                      selectedVehicleFilter === 'car'
                        ? 'bg-blue-600 text-white'
                        : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                    }`}
                  >
                    <Car className="h-3 w-3" />
                    Cars
                  </button>
                  <button
                    onClick={() => setSelectedVehicleFilter('bike')}
                    className={`px-3 py-1 rounded-lg transition-colors flex items-center gap-1 text-sm ${
                      selectedVehicleFilter === 'bike'
                        ? 'bg-green-600 text-white'
                        : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                    }`}
                  >
                    <Bike className="h-3 w-3" />
                    Bikes
                  </button>
                </div>
              </div>
              
              <div className="max-h-60 overflow-y-auto space-y-2 border border-zinc-700 rounded-lg p-3">
                {getFilteredDriftCategories().length > 0 ? (
                  getFilteredDriftCategories().map((category) => (
                    <label key={category.id} className="flex items-start p-3 bg-zinc-800 rounded hover:bg-zinc-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDriftCategories.includes(category.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDriftCategories([...selectedDriftCategories, category.id]);
                          } else {
                            setSelectedDriftCategories(selectedDriftCategories.filter(id => id !== category.id));
                          }
                        }}
                        className="mr-3 mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium">{category.name}</span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            category.vehicle_type === 'car' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {category.vehicle_type}
                          </span>
                          <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800">
                            Drift
                          </span>
                        </div>
                        <div className="text-sm text-gray-400">
                          Fee: {formatCurrency(category.default_registration_fee)} • 
                          {category.use_percentage_prizes ? (
                            <span> Prize: {category.first_place_percentage}% / {category.second_place_percentage}% split</span>
                          ) : (
                            <span> 1st: {formatCurrency(category.default_first_place_prize)}</span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))
                ) : (
                  <p className="text-gray-400 text-center py-4">
                    No drift categories found for {selectedVehicleFilter}s. Create some in Category Management.
                  </p>
                )}
              </div>
              
              <p className="text-xs text-gray-400 mt-2">
                Select specific drift categories for this event.
              </p>
            </div>
          )}

          {/* Drag Event Categories */}
          {!isDriftEvent && (
            <>
              {/* Vehicle Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Vehicle Type Filter
                </label>
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setSelectedVehicleFilter('car')}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                      selectedVehicleFilter === 'car'
                        ? 'bg-primary-600 text-white'
                        : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
                    }`}
                  >
                    <Car className="h-4 w-4" />
                    Cars ({globalCategories.filter(c => c.vehicle_type === 'car' && c.category_type === 'drag').length})
                  </button>
                  <button
                    onClick={() => setSelectedVehicleFilter('bike')}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                      selectedVehicleFilter === 'bike'
                        ? 'bg-primary-600 text-white'
                        : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
                    }`}
                  >
                    <Bike className="h-4 w-4" />
                    Bikes ({globalCategories.filter(c => c.vehicle_type === 'bike' && c.category_type === 'drag').length})
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Select Racing Categories *
                </label>
                <div className="max-h-60 overflow-y-auto space-y-2 border border-zinc-700 rounded-lg p-3">
                  {getFilteredCategories().map((category) => (
                    <label key={category.id} className="flex items-start p-3 bg-zinc-800 rounded hover:bg-zinc-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCategories([...selectedCategories, category.id]);
                          } else {
                            setSelectedCategories(selectedCategories.filter(id => id !== category.id));
                          }
                        }}
                        className="mr-3 mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium">{category.name}</span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            category.vehicle_type === 'car' ? 'bg-primary-100 text-primary-800' : 'bg-primary-100 text-primary-800'
                          }`}>
                            {category.vehicle_type}
                          </span>
                        </div>
                        <div className="text-sm text-gray-400">
                          Fee: {formatCurrency(category.default_registration_fee)} • 
                          {category.use_percentage_prizes ? (
                            <span> Prize: {category.first_place_percentage}% / {category.second_place_percentage}% split</span>
                          ) : (
                            <span> 1st: {formatCurrency(category.default_first_place_prize)}</span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                {selectedTrackImage.track_type === 'drag' && selectedCategories.length === 0 && (
                  <p className="text-primary-400 text-sm mt-2">Please select at least one category</p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="mt-8 flex gap-3">
          <button
            onClick={() => setShowEventModal(false)}
            className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-md font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateEvent}
            disabled={!selectedEventDate || (selectedTrackImage.track_type === 'drag' && selectedCategories.length === 0) || isSubmitting}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-md font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Processing...' : selectedTrackImage.track_type === 'drift' ? 'Create Drift Event' : 'Update Categories'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventModal;